import {
  openScamWarningOverpanelAction,
  closeScamWarningOverpanelAction,
  togglePurposeOfPaymentOverpanel,
} from '../overpanels'
import { getPaymentContingencyHandlers } from './paymentContingencyHandlers'
import {
  getScamWarningType,
  getFundingIdForContingencyResolutions,
} from '../../utils/contingencyUtils'
import { PAYMENT_CONTINGENCY_ACTIONS } from '../../../../../lib/constants'
import { logger } from 'loglevel'
import { post } from '../../../calculator/lib/xhr'
import {
  setContingencyFundingIdAction,
  setPendingResolutionsAction,
  setScamWarningTypeAction,
} from './paymentContingencyActions'
import { ActionTypes as types } from '../../constants'

/**
 * Dispatch payment contingency actions after receiving Funding Options.
 * Actions like displaying an overpanel, showing an alert, etc. See handlers file.
 *
 * Handlers return Promises used to halt the transaction until the customer resolves the
 * contingency. This stores and returns those promises.
 */
export async function handlePaymentContingencies(
  paymentContingencies,
  dispatch
) {
  // Contingency handlers contain the actions required to handle it
  const handlers = getPaymentContingencyHandlers(paymentContingencies)

  // Await each promise one by one, if the handler exists. Promises are created sequentially,
  // a new one created only after previous was resolved.
  for (const contingency of paymentContingencies) {
    const handler = handlers[contingency.action]
    if (handler) {
      await dispatch(handler())
    }
  }
}

/**
 * Dispatch actions for payment contingencies and await for their resolution.
 * For example, opens a Scam Warning overpanel if there is a DISPLAY_SCAM_WARNING contingency, and
 * continue execution when user has resolved.
 */
export function resolvePaymentContingenciesSync(dispatch) {
  return async response => {
    const { data = {} } = response
    const { paymentContingencies = [], fundingOptions = [] } = data

    try {
      if (paymentContingencies.length > 0) {
        const contingencyFundingId = getFundingIdForContingencyResolutions(
          fundingOptions
        )
        dispatch(setContingencyFundingIdAction(contingencyFundingId))

        // Handle and wait for all contingencies to resolve
        await handlePaymentContingencies(paymentContingencies, dispatch)
      }

      return response
    } catch (rejection) {
      // Throw error with the expected 'code' proparty
      const error = new Error('Contingency Rejection')
      error.code = 'contingencyRejection'
      throw error
    }
  }
}

/**
 * Display the Scam Warning overpanel.
 */
export function openScamWarningOverpanel() {
  return (dispatch, getState) => {
    dispatch(openScamWarningOverpanelAction())
  }
}

/**
 * Close the Scam Warning overpanel. If the user continued with payment, the contingency is Resolved.
 */
export function closeScamWarningOverpanel({ isResolved, resolver } = {}) {
  return (dispatch, getState) => {
    if (isResolved) {
      dispatch(resolveScamWarningContingency())
    }
    dispatch(closeScamWarningOverpanelAction())

    // Resolve or reject the promise created by the Scam Warning. Resumes overall execution.
    if (typeof resolver === 'function') {
      resolver()
    }
  }
}

/**
 * Store the risk level (type) of the scam warning. Used for rendering different warning overpanels.
 */
export function setScamWarningType(paymentContingencies = []) {
  return async (dispatch, getState) => {
    const scamWarningType = getScamWarningType(paymentContingencies)
    dispatch(setScamWarningTypeAction(scamWarningType))
  }
}

/**
 * Make a request for resolving Scam Warning contingency to p2pgateway.
 *
 * See https://github.paypal.com/pages/cdjohnston/p2pgatewaystaticdocs/paymentcontingencyresolutioninput.doc.html
 * for resolution input schema.
 */
export function resolveScamWarningContingency() {
  return async (dispatch, getState) => {
    const { transaction = {}, server = {} } = getState()
    const { env = '' } = server
    const { contingencyFundingId } = transaction

    try {
      const contingencyAction = PAYMENT_CONTINGENCY_ACTIONS.DISPLAY_SCAM_WARNING

      // Prop names to match resolution schema
      const contingencyResolution = {
        action: contingencyAction,
        fundingOptionId: contingencyFundingId,
      }

      dispatch(resolveContingency(contingencyResolution))

      const url = '/myaccount/transfer/send/scam-warning-contingency'
      const response = await post(url, { contingencyResolution, env })

      if (response && response.isScamWarningResolved) {
        dispatch(finishContingencyResolution(contingencyResolution))
      }
    } catch (error) {
      // Errors should be very rare and will be handled by middleware.
      logger.error(error)
    }
  }
}

/**
 * Store the fact that the user resolved a contingency. This is used to retry
 * resolution if first attempt failed.
 */
export function resolveContingency(resolution) {
  return async (dispatch, getState) => {
    const { transaction = {} } = getState()
    const { pendingResolutions = [] } = transaction

    // Store resolution as pending while waiting for succesful response
    const updatedResolutions = [...pendingResolutions, resolution]
    dispatch(setPendingResolutionsAction(updatedResolutions))
  }
}

/**
 * Remove contingency resolution from pending list after receiving a successful response.
 */
export function finishContingencyResolution(resolution) {
  return async (dispatch, getState) => {
    const { transaction = {} } = getState()
    const { pendingResolutions = [] } = transaction

    // Remove successful resolution from pending list
    const updatedResolutions = pendingResolutions.filter(
      pending => pending.action !== resolution.action
    )

    dispatch(setPendingResolutionsAction(updatedResolutions))
  }
}

/**
 * Display Purpose of Payment popup
 */
export function openPurposeOfPaymentOverpanel() {
  return dispatch => {
    return new Promise((resolve, reject) => {
      dispatch(
        togglePurposeOfPaymentOverpanel({
          show: true,
          purposeOfPaymentPayload: {
            resolve,
            reject,
          },
        })
      )
    })
  }
}

/**
 * To populate and update react states.
 */
export function populatePurposeOfPaymentDetails(
  dispatch,
  contingency,
  selectedPurposeOfCode
) {
  dispatch({
    type: types.TRANSACTION.SUPPORTED_PURPOSE_OF_CODE,
    data: {
      supportedPurposeCodes:
        contingency?.purposeCodeCollection?.supportedPurposeCodes,
      shouldShowPOPOverlay: contingency?.shouldShowPOPOverlay,
      hasPurposeCodeCollectionRequired: true,
      selectedPurposeOfPaymentOption: selectedPurposeOfCode,
    },
  })
}
